import closeIcon from '@SvgIcon/close.svg'

import get from 'lodash/get'
import Vue from 'vue'

import svgIconHtml from '../shared/svgIconHtml'
import { setupModal } from './_modal'
import { dispatcher } from './dispatcher'
import { getConsentFromCookie, saveConsentToCookie } from './module/cookie_consent/cookie'
import { CookieConsent } from './module/cookie_consent/model/CookieConsent'

const CONSENT_CHANGED_EVENT = 'cookie-consent'

let consentUiApp = null

/**
 * @return {boolean}
 */
function hasCookieConsentQueryParam() {
  const queryString = window.location.search
  if (!queryString) {
    return false
  }

  const queryParams = new URLSearchParams(queryString)
  return queryParams.has('cookie-consent')
}

/**
 * @return {boolean}
 */
function isConsentModuleEnabled() {
  return hasCookieConsentQueryParam() || get(window, ['APP', 'cookie_consent', 'enabled'], false)
}

/**
 * @return {CookieConsent|null}
 */
function getConsent() {
  if (!isConsentModuleEnabled()) {
    return CookieConsent.WithFullConsent()
  }

  return getConsentFromCookie()
}

/**
 * @param {CookieConsent} consent
 */
function updateGtagConsent(consent) {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      ad_user_data: consent.marketing ? 'granted' : 'denied',
      ad_personalization: consent.marketing ? 'granted' : 'denied',
      ad_storage: consent.marketing ? 'granted' : 'denied',
      analytics_storage: consent.analytics ? 'granted' : 'denied',
    })
  }
}

/**
 * @param {CookieConsent} consent
 */
function updateClarityConsent(consent) {
  if (typeof window.clarity === 'function') {
    window.clarity('consentv2', 'update', {
      ad_Storage: consent.marketing ? 'granted' : 'denied',
      analytics_Storage: consent.analytics ? 'granted' : 'denied',
    })
  }
}

const CookieConsentNoticeText = Vue.extend({
  name: 'CookieConsentNoticeText',

  // language=HTML
  template: `
    <span>
      We use cookies to help keep our site safe and running smoothly, as well as to personalize your experience,
      perform marketing, and collect analytics. Some of these are provided by third parties.
      
      <template v-if="cta">
        {{ cta }}
      </template>

      <template v-if="privacyPolicyUrl && termsAndConditionsUrl">
        For more details, please see our
        <a :class="linkClass" :href="privacyPolicyUrl">Privacy Policy</a>
        and
        <a :class="linkClass" :href="termsAndConditionsUrl">Terms and Conditions</a>.
      </template>
      <template v-else-if="privacyPolicyUrl">
        For more information, please see our
        <a :class="linkClass" :href="privacyPolicyUrl">Privacy Policy</a>.
      </template>
      <template v-else-if="termsAndConditionsUrl">
        Your use of this site is subject to our
        <a :class="linkClass" :href="termsAndConditionsUrl">Terms and Conditions</a>.
      </template>
    </span>
  `,

  props: {
    cta: {
      type: String,
      default: '',
    },
    linkClass: {
      type: String,
      default: '',
    },
    privacyPolicyUrl: {
      type: String,
      default: '',
    },
    termsAndConditionsUrl: {
      type: String,
      default: '',
    },
  },
})

/**
 * @param {CookieConsent|null} consent
 * @return {CookieConsent|null}
 */
function cloneConsent(consent) {
  if (!consent) {
    return null
  }

  if (typeof consent.clone === 'function') {
    return consent.clone()
  }

  return new CookieConsent(consent.analytics === true, consent.marketing === true)
}

/**
 * @param {CookieConsent|null} left
 * @param {CookieConsent|null} right
 * @return {boolean}
 */
function isSameConsent(left, right) {
  if (!left && !right) {
    return true
  }

  if (!left || !right) {
    return false
  }

  return left.analytics === right.analytics && left.marketing === right.marketing
}

/**
 * @param {CookieConsent|null} currentConsent
 * @return {CookieConsent}
 */
function buildDraftFromSavedConsent(currentConsent) {
  if (!currentConsent) {
    return CookieConsent.WithFullConsent()
  }

  return cloneConsent(currentConsent)
}

/**
 * @param {CookieConsent} consent
 * @param {boolean} persistConsent
 */
function applyConsentEffects(consent, persistConsent) {
  if (persistConsent) {
    saveConsentToCookie(consent)
  }

  updateGtagConsent(consent)
  updateClarityConsent(consent)
}

/**
 * @param {CookieConsent|null} previousConsent
 * @param {CookieConsent|null} nextConsent
 * @return {boolean}
 */
function shouldReloadAfterRevocation(previousConsent, nextConsent) {
  if (!previousConsent || !nextConsent) {
    return false
  }

  const hadAnalyticsConsent = previousConsent.analytics === true
  const hadMarketingConsent = previousConsent.marketing === true
  const hadOptionalConsent = hadAnalyticsConsent || hadMarketingConsent
  if (!hadOptionalConsent) {
    return false
  }

  const revokedAnalytics = hadAnalyticsConsent && nextConsent.analytics !== true
  if (revokedAnalytics) {
    return true
  }

  return hadMarketingConsent && nextConsent.marketing !== true
}

/**
 * @param {Object|null} ev
 * @return {CookieConsent|null}
 */
function getConsentFromEvent(ev) {
  if (ev && Object.prototype.hasOwnProperty.call(ev, 'consent')) {
    return ev.consent
  }

  return getConsent()
}

/**
 * @param {CookieConsent|null} consent
 */
function dispatchConsentChangedEvent(consent) {
  dispatcher.dispatch(CONSENT_CHANGED_EVENT, {
    consent: cloneConsent(consent),
  })
}

const CookieConsentBanner = Vue.extend({
  name: 'CookieConsentBanner',

  components: {
    CookieConsentNoticeText,
  },

  // language=HTML
  template: `
    <section
      v-show="bannerVisible"
      id="cookie-consent-banner"
      class="cookie-consent-banner"
      role="region"
      aria-label="Cookie consent"
    >
      <div class="cookie-consent-banner__header">
        <span class="cookie-consent-banner__icon" aria-hidden="true">&#127850;</span>
        <span class="cookie-consent-banner__title">Cookie notice</span>
      </div>

      <p class="cookie-consent-banner__message">
        <cookie-consent-notice-text
          :cta="bannerCta"
          link-class="cookie-consent-banner__link"
          :privacy-policy-url="privacyPolicyUrl"
          :terms-and-conditions-url="termsAndConditionsUrl"
        />
      </p>

      <div class="cookie-consent-banner__actions">
        <button
          class="cookie-consent-banner__button cookie-consent-banner__button--secondary"
          type="button"
          @click="openPreferences"
        >
          Customize
        </button>
        <button
          class="cookie-consent-banner__button cookie-consent-banner__button--primary"
          type="button"
          @click="allowAll"
        >
          Allow all
        </button>
      </div>
    </section>
  `,

  props: {
    initialConsent: {
      type: Object,
      default: null,
    },
    forceVisible: {
      type: Boolean,
      default: false,
    },
    dismissRequestKey: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      bannerDismissed: false,
      hadInitialConsent: this.initialConsent !== null,
      savedConsent: cloneConsent(this.initialConsent),
    }
  },

  computed: {
    bannerVisible() {
      if (this.bannerDismissed) {
        return false
      }

      if (this.forceVisible) {
        return true
      }

      if (!this.savedConsent) {
        return true
      }

      if (this.savedConsent.isFullConsent()) {
        return false
      }

      return this.hadInitialConsent === false
    },
    privacyPolicyUrl() {
      return get(window, ['APP', 'cookie_consent', 'privacy_policy_url'], '') || ''
    },
    termsAndConditionsUrl() {
      return get(window, ['APP', 'cookie_consent', 'terms_and_conditions_url'], '') || ''
    },
    bannerCta() {
      return 'Please select "Allow all" or "Customize" to manage your settings.'
    },
  },

  created() {
    dispatcher.addListener(CONSENT_CHANGED_EVENT, this.onConsentChanged)
  },

  watch: {
    dismissRequestKey() {
      this.closeBanner()
    },
  },

  methods: {
    closeBanner() {
      this.bannerDismissed = true
    },
    syncLocalConsentState(consent) {
      this.savedConsent = cloneConsent(consent)
    },
    onConsentChanged(ev) {
      const consent = getConsentFromEvent(ev)
      if (isSameConsent(this.savedConsent, consent)) {
        return
      }

      this.syncLocalConsentState(consent)

      if (consent && consent.isFullConsent()) {
        this.closeBanner()
      }
    },
    openPreferences() {
      this.$emit('open-preferences')
    },
    allowAll() {
      const consent = CookieConsent.WithFullConsent()
      saveConsentToCookie(consent)
      this.syncLocalConsentState(consent)
      this.closeBanner()
      dispatchConsentChangedEvent(consent)
    },
  },
})

const CookieConsentPreferences = Vue.extend({
  name: 'CookieConsentPreferences',

  components: {
    CookieConsentNoticeText,
  },

  // language=HTML
  template: `
    <div
      id="modal-cookie-preferences"
      ref="modal"
      class="reveal with-header cookie-consent-modal"
      data-close-on-click="false"
    >
      <div class="reveal__heading-and-close">
        <div class="reveal__heading">Your privacy choices</div>
        <button class="close-button" data-close aria-label="Close modal" type="button">
          ${svgIconHtml(closeIcon)}
        </button>
      </div>

      <div class="cookie-consent-modal__content">
        <p class="cookie-consent-modal__intro">
          <cookie-consent-notice-text
            :cta="modalCta"
            link-class="cookie-consent-modal__link"
            :privacy-policy-url="privacyPolicyUrl"
            :terms-and-conditions-url="termsAndConditionsUrl"
          />
        </p>

        <div class="cookie-consent-modal__rows" role="group" aria-label="Cookie categories">
          <div class="cookie-consent-modal__row cookie-consent-modal__row--essential">
            <div class="cookie-consent-modal__row-text">
              <div class="cookie-consent-modal__row-title">Essential</div>
              <div class="cookie-consent-modal__row-desc">
                These cookies are needed for our website to function and be secure.
              </div>
            </div>
            <div class="cookie-consent-modal__row-control">
              <span class="cookie-consent-modal__always-on">Always on</span>
            </div>
          </div>

          <div class="cookie-consent-modal__row">
            <label class="cookie-consent-modal__row-text" for="cookie-consent-analytics">
              <div class="cookie-consent-modal__row-title">Analytics</div>
              <div class="cookie-consent-modal__row-desc">
                Cookies that help us understand our visitors and improve their experience.
              </div>
            </label>
            <div class="cookie-consent-modal__row-control">
              <div class="switch tiny cookie-consent-modal__toggle">
                <input
                  id="cookie-consent-analytics"
                  class="switch-input"
                  type="checkbox"
                  name="analytics"
                  v-model="draftConsent.analytics"
                >
                <label class="switch-paddle" for="cookie-consent-analytics">
                  <span class="show-for-sr">Analytics cookies</span>
                </label>
              </div>
            </div>
          </div>

          <div class="cookie-consent-modal__row">
            <label class="cookie-consent-modal__row-text" for="cookie-consent-marketing">
              <div class="cookie-consent-modal__row-title">Marketing</div>
              <div class="cookie-consent-modal__row-desc">
                Cookies that help us display ads that are more relevant to our users.
              </div>
            </label>
            <div class="cookie-consent-modal__row-control">
              <div class="switch tiny cookie-consent-modal__toggle">
                <input
                  id="cookie-consent-marketing"
                  class="switch-input"
                  type="checkbox"
                  name="marketing"
                  v-model="draftConsent.marketing"
                >
                <label class="switch-paddle" for="cookie-consent-marketing">
                  <span class="show-for-sr">Marketing cookies</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="reveal__footer cookie-consent-modal__actions">
          <button class="button hollow cookie-consent-modal__action" type="button" @click="allowAll">
            Accept all
          </button>
          <button class="button hollow cookie-consent-modal__action" type="button" @click="rejectOptional">
            Reject all
          </button>
          <button
            class="button cookie-consent-modal__action cookie-consent-modal__action--primary"
            type="button"
            @click="savePreferences"
          >
            Confirm selection
          </button>
        </div>
      </div>
    </div>
  `,

  props: {
    initialConsent: {
      type: Object,
      default: null,
    },
    openRequestKey: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      modal: null,
      reloadPending: false,
      savedConsent: cloneConsent(this.initialConsent),
      draftConsent: buildDraftFromSavedConsent(this.initialConsent),
    }
  },

  computed: {
    privacyPolicyUrl() {
      return get(window, ['APP', 'cookie_consent', 'privacy_policy_url'], '') || ''
    },
    termsAndConditionsUrl() {
      return get(window, ['APP', 'cookie_consent', 'terms_and_conditions_url'], '') || ''
    },
    modalCta() {
      return 'Use the options below to manage your settings.'
    },
  },

  created() {
    dispatcher.addListener(CONSENT_CHANGED_EVENT, this.onConsentChanged)
  },

  watch: {
    openRequestKey() {
      this.openPreferences()
    },
  },

  methods: {
    syncLocalConsentState(consent) {
      const nextConsent = cloneConsent(consent)
      this.savedConsent = nextConsent
      this.draftConsent = buildDraftFromSavedConsent(nextConsent)
    },
    ensureModal() {
      if (this.modal) {
        return this.modal
      }

      this.modal = setupModal(this.$refs.modal)
      return this.modal
    },
    closePreferences() {
      const modal = this.ensureModal()
      if (!modal) {
        return
      }

      modal.close()
    },
    openPreferences() {
      this.draftConsent = buildDraftFromSavedConsent(this.savedConsent)

      const modal = this.ensureModal()
      if (!modal) {
        return
      }

      modal.open()
    },
    onConsentChanged(ev) {
      const consent = getConsentFromEvent(ev)

      this.closePreferences()

      if (isSameConsent(this.savedConsent, consent)) {
        return
      }

      this.syncLocalConsentState(consent)
    },
    showReloadNoticeAndReload() {
      if (this.reloadPending) {
        return
      }

      this.reloadPending = true

      window.location.reload()
    },
    applyConsentChoice(consent) {
      const normalizedConsent = cloneConsent(consent)
      const shouldReload = shouldReloadAfterRevocation(this.savedConsent, normalizedConsent)

      saveConsentToCookie(normalizedConsent)
      this.syncLocalConsentState(normalizedConsent)
      dispatchConsentChangedEvent(normalizedConsent)
      this.$emit('consent-choice-applied')

      if (shouldReload) {
        this.showReloadNoticeAndReload()
      }
    },
    allowAll() {
      this.applyConsentChoice(CookieConsent.WithFullConsent())
    },
    rejectOptional() {
      this.applyConsentChoice(CookieConsent.WithNoConsent())
    },
    savePreferences() {
      this.applyConsentChoice(this.draftConsent)
    },
  },
})

const CookieConsentUi = Vue.extend({
  name: 'CookieConsentUi',

  components: {
    CookieConsentBanner,
    CookieConsentPreferences,
  },

  // language=HTML
  template: `
    <div>
      <cookie-consent-banner
        @open-preferences="onOpenPreferencesRequest"
        :dismiss-request-key="bannerDismissRequestKey"
        :force-visible="forceVisible"
        :initial-consent="initialConsent"
      />
      <cookie-consent-preferences
        @consent-choice-applied="onPreferencesConsentChoiceApplied"
        :initial-consent="initialConsent"
        :open-request-key="preferencesOpenRequestKey"
      />
    </div>
  `,

  props: {
    initialConsent: {
      type: Object,
      default: null,
    },
    forceVisible: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      bannerDismissRequestKey: 0,
      preferencesOpenRequestKey: 0,
      savedConsent: cloneConsent(this.initialConsent),
    }
  },

  created() {
    dispatcher.addListener(CONSENT_CHANGED_EVENT, this.onConsentChanged)
  },

  methods: {
    onOpenPreferencesRequest() {
      this.preferencesOpenRequestKey += 1
    },
    onPreferencesConsentChoiceApplied() {
      this.bannerDismissRequestKey += 1
    },
    onConsentChanged(ev) {
      const consent = getConsentFromEvent(ev)
      if (isSameConsent(this.savedConsent, consent)) {
        return
      }

      if (consent) {
        applyConsentEffects(consent, false)
      }

      this.savedConsent = cloneConsent(consent)
    },
  },
})

function setupCookieConsent() {
  if (!document.body || consentUiApp) {
    return
  }

  const rootEl = document.getElementById('cookie-consent-app')
  if (rootEl) {
    consentUiApp = new Vue({
      render(createEl) {
        return createEl(CookieConsentUi, {
          props: {
            forceVisible: hasCookieConsentQueryParam(),
            initialConsent: getConsent(),
          },
        })
      },
    })
    consentUiApp.$mount(rootEl)
  }
}

export { CONSENT_CHANGED_EVENT, getConsent, isConsentModuleEnabled, setupCookieConsent }
